import Navbar from "@/components/Navbar";
import ClientBottomNav from "@/components/ClientBottomNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertCircle, Camera, CheckCircle2, LogOut, MapPin, Save } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWilayas, useCommunesByWilaya } from "@/hooks/useAlgeriaData";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

const MonProfil = () => {
  useSEO({ noindex: true });
  const { locale, t } = useLanguage("client");
  const lNavigate = useLocalizedNavigate();
  const allWilayasData = useWilayas(locale);
  const mvpWilayasData = allWilayasData; // toutes les wilayas maintenant disponibles
  const { user, loading: authLoading, signOut } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const [displayName, setDisplayName] = useState("");
  const [wilaya, setWilaya] = useState("");
  const [commune, setCommune] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredContact, setPreferredContact] = useState("inApp");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Communes for selected wilaya — always FR city_name as value for DB compatibility
  const communeOptions = useCommunesByWilaya(wilaya, locale);

  useEffect(() => {
    if (profile) {
      setDisplayName((profile as any).display_name || "");
      setWilaya((profile as any).wilaya || "");
      setCommune((profile as any).commune || "");
      setPhone(profile.phone || "");
      setPreferredContact((profile as any).preferred_contact || "inApp");
      setAvatarPreview((profile as any).avatar_url || null);
    }
  }, [profile]);

  useEffect(() => {
    if (!authLoading && !user) lNavigate("/connexion");
  }, [user, authLoading, lNavigate]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const updateProfile = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");

      let avatarUrl = (profile as any)?.avatar_url || null;

      if (avatarFile) {
        const ext = avatarFile.name.split(".").pop();
        const path = `avatars/${user.id}/${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from("logos").upload(path, avatarFile);
        if (uploadErr) throw uploadErr;
        const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
        avatarUrl = urlData.publicUrl;
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          display_name: displayName,
          wilaya,
          commune,
          phone,
          preferred_contact: preferredContact,
          avatar_url: avatarUrl,
        } as any)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      toast({ title: t("profil_client.toast_saved") });
    },
    onError: (err: any) => {
      toast({ title: t("profil_client.toast_error"), description: err.message, variant: "destructive" });
    },
  });

  // Profil considéré complet si nom + wilaya + commune sont renseignés
  const isProfileComplete = !!(displayName && wilaya && commune);

  if (authLoading || isLoading) return null;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-2xl py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold">{t("profil_client.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("profil_client.subtitle")}</p>
        </div>

        {/* Bannière d'invitation à compléter la localisation */}
        {!isProfileComplete && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {t("profil_client.complete_banner")}
            </p>
          </div>
        )}

        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <label htmlFor="avatar-upload" className="cursor-pointer">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={avatarPreview || undefined} />
                  <AvatarFallback className="bg-primary/10">
                    {avatarPreview ? null : <Camera className="h-6 w-6 text-primary" />}
                  </AvatarFallback>
                </Avatar>
              </label>
              <input id="avatar-upload" type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
              <div>
                <p className="font-display font-semibold">{displayName || t("profil_client.your_name_placeholder")}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                {isProfileComplete && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    <CheckCircle2 className="h-3 w-3" /> {t("profil_client.profile_complete")}
                  </span>
                )}
              </div>
            </div>

            {/* Nom d'affichage */}
            <div className="space-y-2">
              <Label className="font-display font-semibold">{t("profil_client.label_display_name")} *</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t("profil_client.your_name_placeholder")} />
            </div>

            {/* Localisation */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <Label className="font-display font-semibold">{t("profil_client.label_localisation")}</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("profil_client.location_hint")}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{t("profil_client.label_wilaya")}</Label>
                  <Select value={wilaya} onValueChange={(v) => { setWilaya(v); setCommune(""); }}>
                    <SelectTrigger><SelectValue placeholder={t("profil_client.label_wilaya")} /></SelectTrigger>
                    <SelectContent>
                      {mvpWilayasData.map((w) => (
                        <SelectItem key={w.state_name} value={w.state_name}>{w.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{t("profil_client.label_commune")}</Label>
                  <Select value={commune} onValueChange={setCommune} disabled={!wilaya}>
                    <SelectTrigger>
                      <SelectValue placeholder={wilaya ? t("profil_client.label_commune") : t("profil_client.placeholder_commune_disabled")} />
                    </SelectTrigger>
                    <SelectContent>
                      {communeOptions.map((c) => (
                        <SelectItem key={c.city_name} value={c.city_name}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Téléphone */}
            <div className="space-y-2">
              <Label className="font-display font-semibold">{t("profil_client.label_phone")}</Label>
              <Input type="tel" placeholder={t("profil_client.placeholder_phone")} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>

            {/* Mode de contact préféré */}
            <div className="space-y-2">
              <Label className="font-display font-semibold">{t("profil_client.label_contact")}</Label>
              <Select value={preferredContact} onValueChange={setPreferredContact}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inApp">{t("profil_client.contact_app")}</SelectItem>
                  <SelectItem value="phone">{t("profil_client.contact_phone")}</SelectItem>
                  <SelectItem value="whatsapp">{t("profil_client.contact_whatsapp")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="hero"
              className="w-full"
              onClick={() => updateProfile.mutate()}
              disabled={updateProfile.isPending}
            >
              <Save className="me-2 h-4 w-4" />
              {updateProfile.isPending ? t("profil_client.saving") : t("profil_client.save")}
            </Button>

            <Separator />

            <Button
              variant="ghost"
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => signOut()}
            >
              <LogOut className="me-2 h-4 w-4" />
              Déconnexion
            </Button>
          </CardContent>
        </Card>

      </main>
      <Footer />
      <ClientBottomNav />
    </div>
  );
};

export default MonProfil;
